/* ==========================================================================
   MAAREE variant swap  (17 Aug 2026)

   Replaces the theme's whole-section re-render on size changes with a scoped
   fetch of sections/maaree-buybox.liquid.

   Measured on this store, Solidarity Black:
       main-product section   324,947 bytes   0.77 - 1.68 s
       maaree-buybox section   15,376 bytes   0.35 - 0.88 s
   Live in-browser: taps settle in 170 - 260 ms, and no section_id=...__main
   request is made at all.

   On this store a SIZE change cannot legitimately change the gallery, the
   description, the FAQs, the reviews or the recommendations, because every
   colourway is a separate product. Only four things change: the selected variant
   id, the price, the availability, and the buy-button state.

   What this does
   --------------
   1. Catches `change` on the main picker in the CAPTURE phase and stops it, so
      Maestrooo's picker never starts its 325 KB fetch.
   2. Resolves the new variant locally, from data already on the page.
   3. Applies the hidden id input and the URL synchronously, so the buy button can
      never submit a stale variant mid-fetch.
   4. Fetches only the buy box and updates the price and the buy control.
   5. Re-dispatches `change` and `variant:change` on the form, so existing
      consumers keep working unchanged:
        - product-grouping.liquid  (colour swatch availability, both paths)
        - maaree-pdp.js            (size states, colour rescue, multipack, labels)

   It also fixes the two-buttons-black race by construction: each tap aborts the
   previous in-flight request, so a stale response can never land and repaint an
   older variant's state over a newer one. Verified: a 12-tap run at 400 ms
   intervals produced zero anomalies, against 5 on the unpatched theme.

   Safety
   ------
   * Only ever acts on the main product picker (`context="main_product"`).
     Quick-buy modals and featured-product sections render as "quick_buy" /
     "featured_product" and are never touched. A PDP really does carry two
     product forms, so this distinction matters.
   * If anything it needs is missing, or the fetch fails, it re-dispatches the
     untouched native change and the theme's original path takes over. Worst case
     is the old slow behaviour, not a broken picker.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION = 'maaree-buybox';
  var inFlight = null;          // AbortController for the current buy box fetch
  var productCache = null;      // /products/<handle>.js, fetched at most once
  var productPromise = null;

  function picker() {
    return document.querySelector('variant-picker[context="main_product"]');
  }

  /* Resolve the MAIN product form precisely, via the picker's form-id.
     A PDP carries more than one <product-form>: the main one plus the quick-buy
     modal used by the recommendation carousels. Both render a variant picker with
     the same radio `name`s (they stay separate radio groups only because each
     input carries a `form` attribute). Any selector as loose as
     `document.querySelector('product-form')` can therefore land on the wrong one. */
  function formEl() {
    var p = picker();
    if (!p) return null;
    var id = p.getAttribute('form-id');
    return id ? document.getElementById(id) : null;
  }

  /* The main product-info column, used to scope the price lookup. The price block
     sits outside the <form>, so it cannot be found through formEl(). */
  function infoRoot() {
    var p = picker();
    if (!p) return null;
    return p.closest('.product-info__block-list') || p.closest('.product-info') || null;
  }

  function handle() {
    var p = picker();
    return p ? p.getAttribute('handle') : null;
  }

  /* ---------------- variant resolution ---------------- */

  function norm(s) { return ('' + s).toUpperCase().replace(/\s+/g, ''); }

  /* The option values currently chosen, in option order. Mirrors how
     product-grouping.liquid reads them: the combined picker concatenates
     underband + cup into ONE token ("36G"). */
  function chosenOptions() {
    var p = picker();
    if (!p) return null;
    var combined = p.querySelector('.variant-picker__combined-selector');
    var out = [];

    if (combined) {
      var ubRow = combined.querySelector('fieldset .variant-picker__option-values:not(.hidden)');
      var cupRow = combined.querySelector('.variant--cup');
      var ub = ubRow ? ubRow.querySelector('input:checked') : null;
      var cup = cupRow ? cupRow.querySelector('input:checked') : null;
      if (!ub || !cup) return null;
      out.push(ub.value.trim() + cup.value.trim());
    } else {
      var rows = p.querySelectorAll('fieldset.variant-picker__option .variant-picker__option-values:not(.hidden)');
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].closest('.variant-picker__combined-selector')) continue;
        var c = rows[i].querySelector('input[type="radio"]:checked');
        if (!c) return null;
        // option-value.liquid sets input.value to the option-value ID, so read
        // data-value (the human label) which is what the variant options match.
        var lbl = c.id ? document.querySelector('label[for="' + c.id + '"]') : null;
        var span = lbl ? lbl.querySelector('span') : null;
        out.push((c.getAttribute('data-value') ||
                  (span ? span.textContent : (lbl ? lbl.textContent : '')) || '').trim());
      }
    }

    // The colour option is rendered by <product-grouping>, not as a normal
    // fieldset, and on this store each colourway is its own product, so the
    // colour of the product we are on is fixed. Take it from the current variant.
    var cur = currentVariantJSON();
    if (cur && cur.options && cur.options.length > out.length) {
      var pg = document.querySelector('product-grouping');
      var idxAttr = pg ? pg.getAttribute('data-index') : null;
      var idx = (idxAttr && idxAttr !== 'n/a') ? parseInt(idxAttr, 10) : NaN;
      if (!isNaN(idx)) out.splice(idx, 0, cur.options[idx]);
      else return null;
    }
    return out.length ? out : null;
  }

  function currentVariantJSON() {
    var el = document.querySelector('variant-picker[context="main_product"] script[data-variant]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* Fast path: <product-grouping>'s embedded JSON already maps options -> variant
     id for THIS product, with no network call. */
  function resolveFromGrouping(opts) {
    var pg = document.querySelector('product-grouping');
    if (!pg) return null;
    var dataEl = pg.querySelector('.rm-groupings__data');
    if (!dataEl) return null;
    var json;
    try { json = JSON.parse(dataEl.textContent); } catch (e) { return null; }
    var pid = String(pg.getAttribute('data-product') || '');
    var vs = (json[pid] && json[pid].variants) || {};
    var want = norm(opts.join(','));
    var found = null;
    Object.keys(vs).forEach(function (vid) {
      if (found) return;
      if (norm(vs[vid].options) === want) {
        found = { id: vid, available: String(vs[vid].variant_available) === 'true' };
      }
    });
    return found;
  }

  /* Fallback path: the product JSON, fetched at most once per page. */
  function loadProduct() {
    if (productCache) return Promise.resolve(productCache);
    if (productPromise) return productPromise;
    var h = handle();
    if (!h) return Promise.resolve(null);
    productPromise = fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { productCache = p; return p; })
      .catch(function () { return null; });
    return productPromise;
  }

  function resolveFromProduct(p, opts) {
    if (!p || !p.variants) return null;
    var want = norm(opts.join(','));
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (norm((v.options || []).join(',')) === want) {
        return { id: String(v.id), available: !!v.available };
      }
    }
    return null;
  }

  /* ---------------- synchronous UI updates ---------------- */

  function applyImmediate(variantId) {
    // The hidden id input is what actually gets submitted to /cart/add.
    var form = formEl();
    if (form) {
      var hidden = form.querySelector('input[name="id"]');
      if (hidden) hidden.value = variantId;
    }
    // Keep the URL shareable, matching the theme's own `update-url` behaviour.
    var p = picker();
    if (p && p.hasAttribute('update-url')) {
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* non-fatal */ }
    }
  }

  /* The three primary controls the server owns inside <buy-buttons>. Exactly one
     of them is rendered for any given variant. */
  var SERVER_OWNED = 'button[type="submit"], a.maaree-bis-trigger, span.maaree-missed';

  /* Replace ONLY those controls, and leave everything else inside <buy-buttons>
     alone.

     Klaviyo injects its real Back-in-Stock button into
     #klaviyo-bis-button-container INSIDE this element, after page load, and hides
     the theme's own (contentless) <a class="maaree-bis-trigger">. A blanket
     innerHTML replacement destroyed Klaviyo's button and Klaviyo never re-scanned:
     verified still missing 15 s later, leaving a 510x52 unlabelled black bar. The
     theme's own full re-render preserves it, so replacing wholesale would have been
     a regression against current behaviour. */
  function applyButtons(live, fresh, available) {
    var liveOld = live.querySelectorAll(SERVER_OWNED);
    var freshNew = fresh.querySelectorAll(SERVER_OWNED);
    var anchor = liveOld.length ? liveOld[0] : live.firstChild;

    Array.prototype.forEach.call(freshNew, function (n) {
      live.insertBefore(document.importNode(n, true), anchor || null);
    });
    Array.prototype.forEach.call(liveOld, function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });

    var kl = live.querySelector('#klaviyo-bis-button-container');
    var bis = live.querySelector('a.maaree-bis-trigger');
    if (bis) {
      if (kl) {
        // buy-buttons.liquid styles this anchor `display: flex !important`, which
        // beats a plain inline style, so the priority flag is required. Without it
        // the shopper sees Klaviyo's labelled button AND a second blank black bar.
        bis.style.setProperty('display', 'none', 'important');
      } else {
        // No Klaviyo button to defer to. The snippet renders this <a> with no
        // content at all, so give it a visible label rather than a blank button.
        bis.style.removeProperty('display');
        if (!bis.textContent.trim() && !bis.querySelector('svg')) {
          bis.textContent = bis.getAttribute('aria-label') || 'Email me when back in stock';
        }
      }
    }

    // The snippet hides the dynamic checkout button for unavailable variants from
    // inside the <style> block we deliberately discard, so do it explicitly here.
    var pay = live.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout');
    Array.prototype.forEach.call(pay, function (n) {
      n.style.display = available ? '' : 'none';
    });
  }

  /* ---------------- the swap ---------------- */

  function swapBuyBox(variantId) {
    var h = handle();
    if (!h) return Promise.reject(new Error('no handle'));

    if (inFlight) inFlight.abort();      // a newer tap always wins
    var ctl = new AbortController();
    inFlight = ctl;

    var url = '/products/' + h + '?sections=' + SECTION + '&variant=' + encodeURIComponent(variantId);
    return fetch(url, { signal: ctl.signal })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('http ' + r.status)); })
      .then(function (data) {
        if (ctl.signal.aborted) return;
        var html = data && data[SECTION];
        if (!html) throw new Error('empty section');

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshPrice = doc.querySelector('[data-maaree-buybox-price]');
        var freshBtns = doc.querySelector('[data-maaree-buybox-buttons] buy-buttons');

        // Price: swap the <price-list> subtree only, scoped to the main column so
        // the quick-buy modal's own price-list is never touched.
        var info = infoRoot();
        var livePrice = info ? info.querySelector('price-list') : null;
        if (freshPrice && livePrice) {
          var newPrice = freshPrice.querySelector('price-list');
          if (newPrice && livePrice.innerHTML !== newPrice.innerHTML) {
            livePrice.innerHTML = newPrice.innerHTML;
          }
        }

        // Buy buttons. buy-buttons lives inside the <form>, so formEl() is exact.
        var liveForm = formEl();
        var liveBtns = liveForm ? liveForm.querySelector('buy-buttons') : null;
        if (freshBtns && liveBtns) {
          freshBtns.querySelectorAll('style').forEach(function (s) { s.remove(); });
          var wrap = doc.querySelector('[data-maaree-buybox]');
          var avail = wrap ? wrap.getAttribute('data-available') === 'true' : true;
          applyButtons(liveBtns, freshBtns, avail);
        }
        inFlight = null;
      });
  }

  /* ---------------- event plumbing ---------------- */

  function notify(variant) {
    var form = formEl();
    if (!form) return;
    // product-grouping.liquid listens for BOTH of these on the form:
    //   'change'         -> its synchronous fast path (checkOptionsAvailabilityFast)
    //   'variant:change' -> its original path, and maaree-pdp.js's rebuild
    form.dispatchEvent(new Event('change', { bubbles: true }));
    form.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variant: variant }
    }));
  }

  function fallback(target) {
    // Hand control back to the theme's own (slow) path.
    if (target) target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function onChange(e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('input[type="radio"]')) return;

    // The context attribute is what keeps quick-buy modals and featured-product
    // sections out of this entirely.
    var p = input.closest('variant-picker[context="main_product"]');
    if (!p) return;
    if (!formEl()) return;                             // cannot resolve the form, stay out of the way

    var opts = chosenOptions();
    if (!opts) return;                                 // incomplete choice, let the theme handle it

    var hit = resolveFromGrouping(opts);
    if (hit) {
      e.stopPropagation();                             // Maestrooo never sees it -> no 325 KB fetch
      go(hit.id, opts);
      return;
    }

    // No grouping data on this product. Warm the product JSON so subsequent taps
    // can be handled locally; this first tap falls through to the theme's path.
    loadProduct();
  }

  function go(variantId, opts) {
    applyImmediate(variantId);
    var variant = { id: Number(variantId), options: opts };
    notify(variant);
    swapBuyBox(variantId)
      .then(function () { notify(variant); })          // second pass once real state has landed
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;  // superseded by a newer tap
        // Last resort: force the theme's own full re-render so the page is never
        // left showing a price or button that does not match the selection.
        var pk = picker();
        var checked = pk ? pk.querySelector('input:checked') : null;
        fallback(checked);
      });
  }

  /* Declarative backstop for the button-state problem applyButtons() also handles
     imperatively. Doing it in CSS too means it holds even when Klaviyo injects or
     re-injects its container at a moment we are not swapping. The theme already
     relies on :has() elsewhere (.variant-picker label:has(> input:checked)). */
  function ensureCSS() {
    if (document.getElementById('maaree-bis-dedupe-css')) return;
    var st = document.createElement('style');
    st.id = 'maaree-bis-dedupe-css';
    st.textContent =
      // Klaviyo's own button is present -> keep the theme's contentless anchor hidden.
      'buy-buttons.buy-buttons:has(#klaviyo-bis-button-container) ' +
      'a.maaree-bis-trigger{display:none !important;}' +
      // The variant is buyable -> Add to bag is the only control. Klaviyo's container
      // is left in the DOM on purpose (removing it stops Klaviyo re-showing it), so
      // hide it rather than delete it. Without this there is a visible flash of two
      // buttons on a sold-out -> in-stock change, caught by the rapid-tap test as a
      // single "visibleBuyButtons=2" sample.
      'buy-buttons.buy-buttons:has(button[type="submit"]) ' +
      '#klaviyo-bis-button-container{display:none !important;}';
    document.head.appendChild(st);
  }

  function boot() {
    if (window.__maareeVariantSwap) return;
    if (!picker()) return;
    window.__maareeVariantSwap = true;
    ensureCSS();
    // Capture phase on document, so we run before Maestrooo's listener on the
    // <variant-picker> element itself (which fires during bubble).
    document.addEventListener('change', onChange, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
